import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabaseClient';
import { useNavigate } from 'react-router-dom';
import DashboardHeader from './components/DashboardHeader';
import CartaCard from './components/CartaCard';
import LembretesPanel from './components/LembretesPanel';
import CodigoConviteModal from '../admin/acesso-gratuito/components/CodigoConviteModal';
import DesafioSemanal from './components/DesafioSemanal';
import MinhasGravacoes from './components/MinhasGravacoes';
import ExerciciosRecomendados from './components/ExerciciosRecomendados';
import { Link } from 'react-router-dom';
import { verificarSeAdmin } from '../../lib/verificarAdmin';

interface Carta {
  id: string;
  mensagem: string;
  prazo: string;
  data_criacao: string;
  data_entrega: string;
  status_pagamento: string;
  email_enviado: boolean;
  plano?: string;
  status?: string;
  conteudo?: string;
  situacao_atual?: string;
  desejo_futuro?: string;
  prazo_anos?: number;
  lembretes_ativados?: boolean;
}

export default function DashboardPage() {
  const [cartas, setCartas] = useState<Carta[]>([]);
  const [loading, setLoading] = useState(true);
  const [userEmail, setUserEmail] = useState('');
  const [userId, setUserId] = useState('');
  const navigate = useNavigate();
  const [showCodigoModal, setShowCodigoModal] = useState(false);
  const [temAcessoGratuito, setTemAcessoGratuito] = useState(false);
  const [diasRestantesAcesso, setDiasRestantesAcesso] = useState<number | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    checkUserAndLoadCartas();
    verificarAcessoGratuito();
    checkAdmin();
  }, []);

  const checkAdmin = async () => {
    const admin = await verificarSeAdmin();
    setIsAdmin(admin);
  };

  const checkUserAndLoadCartas = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        navigate('/login');
        return;
      }

      setUserEmail(user.email || '');
      setUserId(user.id);

      // Verificar se o onboarding foi concluído
      const { data: profile } = await supabase
        .from('profiles')
        .select('onboarding_completo')
        .eq('id', user.id)
        .maybeSingle();

      if (profile && profile.onboarding_completo === false) {
        navigate('/onboarding');
        return;
      }

      // Buscar cartas do usuário
      const { data, error } = await supabase
        .from('cartas')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setCartas(data || []);
    } catch (error) {
      console.error('Erro ao carregar cartas:', error);
    } finally {
      setLoading(false);
    }
  };

  const verificarAcessoGratuito = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profile } = await supabase
        .from('profiles')
        .select('acesso_gratuito, acesso_gratuito_expira_em')
        .eq('id', user.id)
        .maybeSingle();

      const temAcesso = profile?.acesso_gratuito || false;
      setTemAcessoGratuito(temAcesso);

      // Calcular dias restantes se tiver acesso gratuito
      if (temAcesso && profile?.acesso_gratuito_expira_em) {
        const dataExpiracao = new Date(profile.acesso_gratuito_expira_em);
        const agora = new Date();
        const diffTime = dataExpiracao.getTime() - agora.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        if (diffDays > 0) {
          setDiasRestantesAcesso(diffDays);
        } else {
          // Acesso expirado - desativar
          await supabase
            .from('profiles')
            .update({ acesso_gratuito: false })
            .eq('id', user.id);
          
          setTemAcessoGratuito(false);
          setDiasRestantesAcesso(null);
          
          // Redirecionar para planos
          navigate('/planos?expired=true');
        }
      }
    } catch (error) {
      console.error('Erro ao verificar acesso gratuito:', error);
      setTemAcessoGratuito(false);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-charcoal-950 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line text-5xl text-gold-400 animate-spin"></i>
          <p className="font-body text-base text-cream-100/60 mt-4 tracking-wide">
            Carregando suas cartas...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-charcoal-950 via-charcoal-900 to-charcoal-950">
      <DashboardHeader userEmail={userEmail} onLogout={handleLogout} />

      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Banner Admin */}
        {isAdmin && (
          <div className="mb-8 rounded-2xl p-6 border bg-gradient-to-r from-gold-500/10 to-gold-300/10 border-gold-500/25">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 rounded-xl flex items-center justify-center bg-gradient-to-br from-amber-500 to-yellow-500">
                <i className="ri-shield-star-line text-4xl text-white"></i>
              </div>
              <div>
                <h3 className="text-white text-3xl font-bold mb-1">👑 Acesso Administrador</h3>
                <p className="text-white/80 text-xl">Você tem acesso total a todas as funcionalidades do site</p>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-4">
              <Link to="/admin/painel" className="px-4 py-3 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 rounded-xl text-white text-lg font-bold text-center transition-all cursor-pointer whitespace-nowrap shadow-lg">
                <i className="ri-dashboard-3-line mr-2"></i>Painel Admin
              </Link>
              <Link to="/admin/acesso-gratuito" className="px-4 py-3 bg-white/10 hover:bg-white/20 rounded-xl text-white text-lg font-medium text-center transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-ticket-line mr-2"></i>Códigos
              </Link>
              <Link to="/admin/vitorias" className="px-4 py-3 bg-white/10 hover:bg-white/20 rounded-xl text-white text-lg font-medium text-center transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-trophy-line mr-2"></i>Vitórias
              </Link>
              <Link to="/admin/teste-email" className="px-4 py-3 bg-white/10 hover:bg-white/20 rounded-xl text-white text-lg font-medium text-center transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-mail-line mr-2"></i>Emails
              </Link>
              <Link to="/admin/configurar-resend" className="px-4 py-3 bg-white/10 hover:bg-white/20 rounded-xl text-white text-lg font-medium text-center transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-settings-3-line mr-2"></i>Configurações
              </Link>
            </div>
          </div>
        )}

        {/* Banner de Acesso Gratuito - esconder para admin */}
        {!isAdmin && temAcessoGratuito && diasRestantesAcesso !== null && (
          <div className={`mb-8 rounded-2xl p-6 border ${
            diasRestantesAcesso <= 2
              ? 'bg-gradient-to-r from-red-500/10 to-orange-500/10 border-red-500/25'
              : 'bg-gradient-to-r from-gold-500/10 to-cream-200/10 border-gold-500/25'
          }`}>
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
                  diasRestantesAcesso <= 2
                    ? 'bg-gradient-to-br from-red-500 to-orange-500'
                    : 'bg-gradient-to-br from-gold-600 to-gold-400'
                }`}>
                  <i className="ri-vip-crown-line text-4xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-white text-3xl font-bold mb-1">
                    {diasRestantesAcesso <= 2 ? '⚠️ Acesso Gratuito Expirando!' : '🎉 Acesso Gratuito Ativo'}
                  </h3>
                  <p className="text-white/80 text-xl">
                    {diasRestantesAcesso === 1 
                      ? 'Último dia de acesso gratuito' 
                      : `${diasRestantesAcesso} dias restantes de acesso gratuito`}
                  </p>
                </div>
              </div>
              
              {diasRestantesAcesso <= 2 && (
                <button
                  onClick={() => navigate('/planos')}
                  className="px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-lg font-semibold rounded-xl hover:shadow-xl transition-all duration-300 whitespace-nowrap cursor-pointer animate-pulse"
                >
                  <i className="ri-star-line mr-2"></i>
                  Assinar Agora
                </button>
              )}
            </div>
            
            {/* Barra de progresso */}
            <div className="mt-4 bg-white/10 rounded-full h-2 overflow-hidden">
              <div 
                className={`h-full transition-all duration-500 ${
                  diasRestantesAcesso <= 2 
                    ? 'bg-gradient-to-r from-red-500 to-orange-500' 
                    : 'bg-gradient-to-r from-purple-500 to-pink-500'
                }`}
                style={{ width: `${(diasRestantesAcesso / 9) * 100}%` }}
              ></div>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {/* Novo Card - Caderno da Gratidão */}
          <div className="bg-gradient-to-br from-gold-500/10 to-gold-300/10 backdrop-blur-sm rounded-2xl p-6 border border-gold-500/20">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-gold-600 to-gold-400 rounded-xl flex items-center justify-center">
                <i className="ri-heart-3-fill text-3xl text-charcoal-900"></i>
              </div>
              <button
                onClick={() => navigate('/gratidao')}
                className="text-gold-400 hover:text-gold-300 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-right-line text-3xl"></i>
              </button>
            </div>
            <h3 className="text-cream-100 text-2xl font-semibold mb-1">Caderno da Gratidão</h3>
            <p className="text-gold-300 text-xl">Cultive a gratidão diariamente</p>
          </div>

          {/* Cards extras para Admin */}
          {isAdmin && (
            <>
              <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 backdrop-blur-sm rounded-2xl p-6 border border-green-500/20">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center">
                    <i className="ri-book-2-fill text-3xl text-white"></i>
                  </div>
                  <button onClick={() => navigate('/diario')} className="text-green-400 hover:text-green-300 transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-arrow-right-line text-3xl"></i>
                  </button>
                </div>
                <h3 className="text-white text-2xl font-semibold mb-1">Diário Digital</h3>
                <p className="text-green-300 text-xl">Acesso completo</p>
              </div>

              <div className="bg-gradient-to-br from-blush-300/10 to-blush-400/10 backdrop-blur-sm rounded-2xl p-6 border border-blush-300/20">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blush-500 to-blush-400 rounded-xl flex items-center justify-center">
                    <i className="ri-fire-fill text-3xl text-white"></i>
                  </div>
                  <button onClick={() => navigate('/programa-diario')} className="text-blush-300 hover:text-blush-200 transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-arrow-right-line text-3xl"></i>
                  </button>
                </div>
                <h3 className="text-cream-100 text-2xl font-semibold mb-1">Programa Diário</h3>
                <p className="text-blush-300 text-xl">Acesso completo</p>
              </div>

              <div className="bg-gradient-to-br from-gold-500/10 to-cream-200/10 backdrop-blur-sm rounded-2xl p-6 border border-gold-500/20">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-gold-600 to-gold-400 rounded-xl flex items-center justify-center">
                    <i className="ri-brain-fill text-3xl text-charcoal-900"></i>
                  </div>
                  <button onClick={() => navigate('/planos')} className="text-gold-400 hover:text-gold-300 transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-arrow-right-line text-3xl"></i>
                  </button>
                </div>
                <h3 className="text-cream-100 text-2xl font-semibold mb-1">Exercícios PNL</h3>
                <p className="text-gold-300 text-xl">Todos liberados</p>
              </div>
            </>
          )}
        </div>

        {/* ✨ DESAFIO SEMANAL */}
        {userId && <DesafioSemanal userId={userId} />}

        {/* 🎧 MINHAS GRAVAÇÕES */}
        <MinhasGravacoes />

        {/* 💡 EXERCÍCIOS RECOMENDADOS */}
        <ExerciciosRecomendados />

        {/* 🛍️ HISTÓRICO DE COMPRAS */}
        <div className="mb-8">
          <Link to="/dashboard/compras">
            <div className="relative bg-white/5 hover:bg-white/8 backdrop-blur-sm rounded-2xl p-6 border border-gold-500/15 hover:border-gold-500/30 transition-all duration-300 cursor-pointer group">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-gradient-to-br from-gold-600/30 to-gold-400/20 border border-gold-500/25 text-2xl">
                    🧾
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-cream-100 font-['Playfair_Display'] mb-0.5">
                      Histórico de Compras
                    </h3>
                    <p className="text-cream-100/50 text-sm">
                      Veja todos os exercícios que você adquiriu e acesse-os diretamente
                    </p>
                  </div>
                </div>
                <div className="hidden md:flex items-center gap-2 text-gold-400/70 group-hover:text-gold-400 transition-colors whitespace-nowrap text-sm font-semibold">
                  Ver histórico
                  <i className="ri-arrow-right-line text-lg" />
                </div>
              </div>
            </div>
          </Link>
        </div>

        {/* NOVO: Card do Diário Digital */}
        <div className="mb-8">
          <Link to="/diario">
            <div className="relative bg-white/5 hover:bg-white/8 backdrop-blur-sm rounded-2xl p-6 border border-gold-500/25 hover:border-gold-400/50 transition-all duration-300 hover:shadow-lg cursor-pointer group">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 flex items-center justify-center rounded-xl bg-gradient-to-br from-gold-600 to-gold-400 text-3xl">
                    📔
                  </div>
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="text-2xl font-semibold text-cream-100 font-['Playfair_Display']">
                        Diário Digital de Conquistas
                      </h3>
                      {isAdmin ? (
                        <span className="text-xs bg-gold-500/20 text-gold-400 border border-gold-500/30 px-2 py-0.5 rounded-full font-semibold tracking-wider uppercase">
                          ADMIN
                        </span>
                      ) : (
                        <span className="text-xs bg-cream-100/10 text-cream-200 border border-cream-100/20 px-2 py-0.5 rounded-full font-semibold tracking-wider uppercase">
                          NOVO
                        </span>
                      )}
                    </div>
                    <p className="text-cream-100/50 text-base">
                      Planeje suas metas, acompanhe etapas e conquiste troféus
                    </p>
                    <div className="text-gold-400 font-medium mt-1 text-sm">
                      {isAdmin ? 'Acesso Total Liberado ✓' : 'R$ 18/mês • Gamificação + Lembretes no WhatsApp'}
                    </div>
                  </div>
                </div>
                <div className="hidden md:flex items-center gap-2 bg-gold-500/15 border border-gold-500/30 text-gold-400 font-semibold px-6 py-3 rounded-xl whitespace-nowrap text-sm group-hover:bg-gold-500/25 transition-all">
                  ACESSAR AGORA
                  <i className="ri-arrow-right-line text-lg"></i>
                </div>
              </div>
            </div>
          </Link>
        </div>

        {/* NOVO: Card do Programa Diário */}
        <div className="mb-8">
          <Link to="/programa-diario">
            <div className="relative bg-white/5 hover:bg-white/8 backdrop-blur-sm rounded-2xl p-6 border border-cream-100/15 hover:border-cream-100/30 transition-all duration-300 hover:shadow-lg cursor-pointer group">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 flex items-center justify-center rounded-xl bg-gradient-to-br from-charcoal-700 to-charcoal-600 border border-cream-100/15 text-3xl">
                    🔥
                  </div>
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="text-2xl font-semibold text-cream-100 font-['Playfair_Display']">
                        Programa Diário de Sucesso
                      </h3>
                      {isAdmin ? (
                        <span className="text-xs bg-gold-500/20 text-gold-400 border border-gold-500/30 px-2 py-0.5 rounded-full font-semibold tracking-wider uppercase">
                          ADMIN
                        </span>
                      ) : (
                        <span className="text-xs bg-cream-100/10 text-cream-200 border border-cream-100/20 px-2 py-0.5 rounded-full font-semibold tracking-wider uppercase">
                          NOVO
                        </span>
                      )}
                    </div>
                    <p className="text-cream-100/50 text-base">
                      5 exercícios de alta performance para começar seu dia com poder
                    </p>
                    <div className="text-cream-100/40 font-medium mt-1 text-sm">
                      {isAdmin ? 'Acesso Total Liberado ✓' : 'R$ 18/mês • Lembretes no WhatsApp'}
                    </div>
                  </div>
                </div>
                <div className="hidden md:flex items-center gap-2 bg-white/8 border border-cream-100/20 text-cream-100/70 font-semibold px-6 py-3 rounded-xl whitespace-nowrap text-sm group-hover:bg-white/15 group-hover:text-cream-100 transition-all">
                  ACESSAR AGORA
                  <i className="ri-arrow-right-line text-lg"></i>
                </div>
              </div>
            </div>
          </Link>
        </div>

        {/* Título da seção */}
        <div className="mb-12">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-px bg-gradient-to-r from-gold-500 to-gold-300"></div>
            <span className="font-body text-base tracking-[0.3em] uppercase text-gold-400">
              Suas Cartas
            </span>
          </div>
          <h1 className="font-display text-6xl lg:text-7xl text-cream-100 mb-4">
            Mensagens do <span className="text-gold-gradient italic">Futuro</span>
          </h1>
          <p className="font-body text-xl text-cream-100/60 tracking-wide max-w-2xl">
            Acompanhe suas cartas e veja o progresso da sua jornada de transformação
          </p>
        </div>

        {/* Painel de Lembretes */}
        {cartas.length > 0 && (
          <LembretesPanel cartas={cartas} onRefresh={checkUserAndLoadCartas} />
        )}

        {/* Lista de cartas */}
        {cartas.length === 0 ? (
          <div className="text-center py-20">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gold-500/10 border border-gold-500/30 mb-6">
              <i className="ri-mail-line text-4xl text-gold-400"></i>
            </div>
            <h3 className="font-display text-2xl text-cream-100 mb-3">
              Nenhuma carta ainda
            </h3>
            <p className="font-body text-base text-cream-100/60 mb-8 tracking-wide">
              Comece sua jornada de transformação criando sua primeira carta
            </p>
            <a
              href="/#formulario"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-gold-600 to-gold-400 text-charcoal-900 font-body font-semibold text-base px-8 py-4 rounded-lg hover:shadow-gold-lg transition-all duration-500 hover:scale-105 whitespace-nowrap cursor-pointer tracking-wider uppercase"
            >
              <i className="ri-quill-pen-line"></i>
              Escrever minha carta
            </a>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {cartas.map((carta) => (
              <CartaCard key={carta.id} carta={carta} onRefresh={checkUserAndLoadCartas} />
            ))}
          </div>
        )}
      </div>

      {/* Botão de código de convite (esconder para admin) */}
      {!isAdmin && !temAcessoGratuito && (
        <div className="fixed bottom-6 right-6 z-40">
          <button
            onClick={() => setShowCodigoModal(true)}
            className="group flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-gold-600 to-gold-500 text-charcoal-900 rounded-full shadow-gold hover:shadow-gold-lg transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-ticket-line text-xl group-hover:rotate-12 transition-transform"></i>
            <span className="font-semibold">Tenho um Código</span>
          </button>
        </div>
      )}

      {/* Modal de código de convite */}
      {showCodigoModal && (
        <CodigoConviteModal
          onClose={() => setShowCodigoModal(false)}
          onSuccess={() => {
            setShowCodigoModal(false);
            setTemAcessoGratuito(true);
            // Redirecionar para página de boas-vindas
            setTimeout(() => {
              window.location.href = '/boas-vindas-gratuito';
            }, 500);
          }}
        />
      )}
    </div>
  );
}